// MarkAttendance.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../AuthContext';
import configData from '../../configure';
import { getFacultyAssignedClassNames, filterClassesForFaculty } from '../../utils/facultyHierarchyUtils';
import {
  getStoredAttendance,
  saveStoredAttendance,
  getStoredCorrections,
  saveStoredCorrections,
  getStoredSettings,
  DEFAULT_CLASSES,
  DEFAULT_SUBJECTS,
  DEFAULT_STUDENTS,
  seedLiveRosterHistory
} from './attendanceSeed';
import {
  fetchAttendanceRecords,
  createAttendanceRecord,
  updateAttendanceRecord,
  deleteAttendanceRecord
} from '../../services/attendanceService';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './MarkAttendance.css';
// Material Icons
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ScheduleIcon from '@mui/icons-material/Schedule';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import SearchIcon from '@mui/icons-material/Search';
import WarningIcon from '@mui/icons-material/Warning';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SecurityIcon from '@mui/icons-material/Security';

const mapStatusToBackend = (status) => {
  switch (status) {
    case 'Present': return 'PRESENT';
    case 'Absent': return 'ABSENT';
    case 'Late': return 'LATE';
    case 'Leave': return 'ON_LEAVE';
    default: return 'PRESENT';
  }
};

const mapStatusFromBackend = (status) => {
  switch (status) {
    case 'PRESENT': return 'Present';
    case 'ABSENT': return 'Absent';
    case 'LATE': return 'Late';
    case 'ON_LEAVE': return 'Leave';
    default: return status || 'Present';
  }
};

const getDistanceInMeters = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3; // metres
  const phi1 = (lat1 * Math.PI) / 180;
  const phi2 = (lat2 * Math.PI) / 180;
  const deltaPhi = ((lat2 - lat1) * Math.PI) / 180;
  const deltaLambda = ((lon2 - lon1) * Math.PI) / 180;

  const a = Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
            Math.cos(phi1) * Math.cos(phi2) *
            Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // in metres
};

const MarkAttendance = ({ orgId }) => {
  const { axios, token, userRole, user, parentOrgId } = useAuth();
  
  const isAdmin = userRole === 'ADMIN';
  const isFaculty = userRole === 'FACULTY';
  const isStaff = userRole === 'STAFF';
  const isManagement = isAdmin || isStaff;

  const isCustomOrg = orgId === '62d3b1f4-e453-4933-bfcf-7b5adf5b4e83';
  const effectiveParentOrgId = parentOrgId || user?.parentOrgId || user?.orgDetails?.parentOrgId || sessionStorage.getItem('parentOrgId') || localStorage.getItem('parentOrgId') || '';
  const isSmallOrg = effectiveParentOrgId === 'SMALLORG-001';

  // Selected Filters
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split('T')[0]);
  
  const getTodayStr = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const getYesterdayStr = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const isToday = selectedDate === getTodayStr() || selectedDate === getYesterdayStr();
  const [selectedCenter, setSelectedCenter] = useState('');
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');

  // Dropdown lists
  const [classesList, setClassesList] = useState([]);
  const [subjectsList, setSubjectsList] = useState([]);

  const uniqueCenters = React.useMemo(() => {
    if (isCustomOrg) {
      return [...new Set(classesList.map(c => c.label || c.name || c.className).filter(Boolean))];
    }
    return [...new Set(classesList.map(c => c.sectionName || c.label || c.name).filter(Boolean))];
  }, [classesList, isCustomOrg]);

  useEffect(() => {
    if (uniqueCenters.length > 0) {
      if (!selectedCenter || !uniqueCenters.includes(selectedCenter)) {
        setSelectedCenter(uniqueCenters[0]);
      }
    }
  }, [uniqueCenters, selectedCenter]);

  const matchingClasses = React.useMemo(() => {
    if (isCustomOrg) {
      return classesList.filter(c => (c.label || c.name || c.className) === selectedCenter);
    } else {
      const cls = classesList.find(c => c.classId === selectedClassId);
      return cls ? [cls] : [];
    }
  }, [isCustomOrg, classesList, selectedCenter, selectedClassId]);
  
  // Student roster state
  const [students, setStudents] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState({}); // { studentId: { status, remark } }
  
  // Search query
  const [searchQuery, setSearchQuery] = useState('');
  
  // Roster status (final, draft, or new)
  const [recordState, setRecordState] = useState('new'); // 'new', 'draft', 'final'
  const [recordDetails, setRecordDetails] = useState(null);

  // States
  const [isLoading, setIsLoading] = useState(false);
  const [isRosterLoading, setIsRosterLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Settings
  const [settings, setSettings] = useState(null);

  // Nirvahak lookup and selection states
  const [allNirvahaksList, setAllNirvahaksList] = useState([]);
  const [selectedNirvahak, setSelectedNirvahak] = useState(null);
  const [isNirvahakModalOpen, setIsNirvahakModalOpen] = useState(false);
  const [nirvahakSearchQuery, setNirvahakSearchQuery] = useState('');
  const [gpsVerified, setGpsVerified] = useState(false);
  const [gpsVerifying, setGpsVerifying] = useState(false);

  // Geofence states
  const [targetLocation, setTargetLocation] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationStatus, setLocationStatus] = useState('not_configured');
  const [distance, setDistance] = useState(null);
  const [showBypassModal, setShowBypassModal] = useState(false);
  const [bypassReason, setBypassReason] = useState('');
  const [pendingSaveMode, setPendingSaveMode] = useState(null);
  const [assignedNirvahak, setAssignedNirvahak] = useState(null);

  // Correction request modal
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [correctionData, setCorrectionData] = useState({
    studentId: '',
    studentName: '',
    admissionNumber: '',
    oldStatus: '',
    newStatus: 'Present',
    reason: ''
  });

  // Fetch initial classes and subjects
  useEffect(() => {
    const loadFilters = async () => {
      setIsLoading(true);
      setError(null);
      
      const loadedSettings = getStoredSettings();
      setSettings(loadedSettings);

      try {
        let classes = [];
        let subjects = [];

        // Attempt live API for classes
        if (orgId && token) {
          try {
            const res = await axios.get(`${configData.GET_CLASSES_BY_ORG}/${orgId}/metadata`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const isCustomOrg = orgId === '62d3b1f4-e453-4933-bfcf-7b5adf5b4e83';
            const rawClasses = res.data?.items || res.data || [];
            classes = rawClasses.map(c => {
              const centerName = isCustomOrg
                ? (c.className || c.label || c.name || '').trim()
                : (c.name || (c.sectionName && c.sectionName !== 'null' && c.sectionName !== 'undefined' ? `${c.className || ''}-${c.sectionName}` : c.className || '')).trim();
              return {
                classId: c.classId || c.id,
                value: c.classId || c.id,
                className: c.className || centerName,
                sectionName: isCustomOrg ? '' : (c.sectionName || ''),
                name: centerName || 'Unknown Center',
                label: centerName || 'Unknown Center'
              };
            });

            const assignedClassNames = await getFacultyAssignedClassNames(orgId, user, axios);
            classes = filterClassesForFaculty(classes, assignedClassNames);
          } catch (e) {
            console.warn("Failed live classes fetch, falling back to seed", e);
          }
        }
        
        // If API returns empty or failed (and not in custom org hierarchy scoping), use defaults
        const isCustomOrg = orgId === '62d3b1f4-e453-4933-bfcf-7b5adf5b4e83';
        if (!isCustomOrg && (!classes || classes.length === 0)) {
          classes = DEFAULT_CLASSES;
        }
        
        // Attempt live API for subjects
        if (orgId && token) {
          try {
            const res = await axios.get(`${configData.GET_SUBJECTS_BY_ORG}/${orgId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const rawSubjects = res.data?.items || res.data || [];
            subjects = rawSubjects.map(sub => ({
              courseCode: sub.courseCode || sub.id,
              courseName: sub.courseDetails?.courseName || sub.name || 'Unknown Subject',
              period: sub.period || 'General'
            }));
          } catch (e) {
            console.warn("Failed live subjects fetch, falling back to seed", e);
          }
        }

        if (!subjects || subjects.length === 0) {
          subjects = DEFAULT_SUBJECTS;
        }

        setClassesList(classes);
        setSubjectsList(subjects);
        
        // Select first center and subject by default
        const calculatedCenters = [...new Set(classes.map(c => c.sectionName).filter(Boolean))];
        if (calculatedCenters.length > 0) setSelectedCenter(calculatedCenters[0]);
        if (classes.length > 0) setSelectedClassId(classes[0].classId);
        if (subjects.length > 0) setSelectedSubject(subjects[0].courseCode);

      } catch (err) {
        console.error("Error setting up Mark Attendance page", err);
        setError("Unable to initialize form parameters.");
        setClassesList(DEFAULT_CLASSES);
        setSubjectsList(DEFAULT_SUBJECTS);
        const calculatedCenters = [...new Set(DEFAULT_CLASSES.map(c => c.sectionName).filter(Boolean))];
        if (calculatedCenters.length > 0) setSelectedCenter(calculatedCenters[0]);
        if (DEFAULT_CLASSES.length > 0) setSelectedClassId(DEFAULT_CLASSES[0].classId);
        setSelectedSubject(DEFAULT_SUBJECTS[0].courseCode);
      } finally {
        setIsLoading(false);
      }
    };

    loadFilters();
  }, [orgId, token, axios]);

  // Load roster once Date, Center/Class, and Subject are selected
  const loadRoster = useCallback(async () => {
    if ((isCustomOrg && !selectedCenter) || (!isCustomOrg && !selectedClassId) || (!isSmallOrg && !selectedSubject) || !selectedDate) return;
    
    setIsRosterLoading(true);
    setError(null);
    setSearchQuery('');
    
    try {
      // 1. Fetch Students for all matching classes in parallel
      const studentPromises = matchingClasses.map(async (cls) => {
        let studentList = [];
        if (token) {
          try {
            const res = await axios.get(`${configData.GET_STUDENTS_BY_CLASS}/${cls.classId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const rawStudents = res.data?.items || res.data || [];
            studentList = rawStudents.map(item => {
              if (item.id && item.name && item.admissionNumber) {
                return { ...item, classId: cls.classId, className: cls.name };
              }
              const userObj = item.user || {};
              const studentInfoObj = item.studentInfo || {};
              return {
                id: userObj.userId || item.id || '',
                name: `${userObj.firstName || ''} ${userObj.lastName || ''}`.trim() || item.name || 'Unknown Student',
                admissionNumber: studentInfoObj.admissionId || item.admissionNumber || 'N/A',
                active: userObj.active !== false && userObj.status !== 'Inactive',
                status: userObj.status || 'Active',
                remark: item.remark || '',
                classId: cls.classId,
                className: cls.name
              };
            });
          } catch (e) {
            console.warn(`Failed live students fetch for class ${cls.classId}`, e);
          }
        }

        if (!studentList || studentList.length === 0) {
          const rawSeed = DEFAULT_STUDENTS[cls.classId] || [];
          studentList = rawSeed.map(item => ({
            id: item.id,
            name: item.name,
            admissionNumber: item.admissionNumber,
            active: item.active !== false && item.status !== 'Inactive',
            status: item.status || 'Active',
            remark: item.remark || '',
            classId: cls.classId,
            className: cls.name
          }));
        }
        return studentList;
      });

      const studentResults = await Promise.all(studentPromises);
      const combinedStudents = studentResults.flat();

      // Filter out inactive/transferred students
      const activeStudents = combinedStudents.filter(
        s => s.active && s.status !== "Inactive" && s.remark !== "Transferred"
      );
      
      // 2. Check if attendance already exists in backend or fallback to LocalStorage
      let combinedRecords = {};
      let anyRecordsFound = false;

      const recordPromises = matchingClasses.map(async (cls) => {
        if (token) {
          try {
            const backendResult = await fetchAttendanceRecords({
              filterType: 'class',
              id: cls.classId,
              startDate: selectedDate,
              endDate: selectedDate,
              pageSize: 100
            });
            const rawBackendList = backendResult?.items || backendResult || [];
            return { classId: cls.classId, records: rawBackendList };
          } catch (e) {
            console.warn(`Failed backend attendance fetch for class ${cls.classId}`, e);
          }
        }
        return { classId: cls.classId, records: [] };
      });

      const recordResults = await Promise.all(recordPromises);

      recordResults.forEach(res => {
        const rawBackendList = res.records;
        if (rawBackendList.length > 0) {
          anyRecordsFound = true;
          rawBackendList.forEach(rec => {
            if (rec.userId) {
              combinedRecords[rec.userId] = {
                status: mapStatusFromBackend(rec.attendanceStatus),
                remark: rec.remarks || '',
                attendanceId: rec.attendanceId
              };
            }
          });
        }
      });

      if (anyRecordsFound) {
        setRecordState('final'); // treat as finalized if records exist on DB
      } else {
        setRecordState('new');
        setRecordDetails(null);
      }

      // Keep missing active students unselected by default
      activeStudents.forEach(s => {
        if (!combinedRecords[s.id]) {
          combinedRecords[s.id] = {
            status: '',
            remark: '',
            attendanceId: null
          };
        }
      });

      setStudents(activeStudents);
      setAttendanceRecords(combinedRecords);

      // 3. Resolve target coordinates for geofence validation
      let facultyList = [];
      if (token) {
        try {
          const res = await axios.get(`${configData.GET_USERS_BY_TYPE}/${orgId}`, {
            headers: { Authorization: `Bearer ${token}`, accept: 'application/hal+json' },
            params: {
              userType: 'FACULTY',
              pageSize: 150,
            },
          });
          const rawData = res.data?.items || res.data || [];
          facultyList = Array.isArray(rawData) ? rawData : (Array.isArray(rawData.items) ? rawData.items : (Array.isArray(rawData.content) ? rawData.content : []));
        } catch (e) {
          console.warn("Failed to fetch faculty for location check", e);
        }
      }

      const cleanCenterVal = (isCustomOrg ? selectedCenter : (classesList.find(c => c.classId === selectedClassId)?.sectionName || '')).toLowerCase().trim();
      const cleanClassNameVal = (isCustomOrg ? '' : (classesList.find(c => c.classId === selectedClassId)?.className || '')).toLowerCase().trim();
      
      const matchingNirvahaks = facultyList.filter(f => {
        const type = f.facultyInfo?.facultyType || f.raw?.facultyInfo?.facultyType || '';
        if (type !== 'NIRVAHAK') return false;

        const assignedClassesStr = f.user?.additionalAttributes?.assignedClasses || 
                                   f.additionalAttributes?.assignedClasses || 
                                   f.raw?.user?.additionalAttributes?.assignedClasses || '';
        
        const assignedClasses = assignedClassesStr.split(',').map(c => c.trim().toLowerCase());
        
        return (cleanCenterVal && assignedClasses.includes(cleanCenterVal)) || 
               (cleanClassNameVal && assignedClasses.includes(cleanClassNameVal)) ||
               (selectedClassId && assignedClasses.includes(selectedClassId.toLowerCase()));
      });

      let foundLat = null;
      let foundLon = null;
      let foundNirvahakName = '';

      for (const nir of matchingNirvahaks) {
        const attributes = nir.user?.additionalAttributes || nir.additionalAttributes || nir.raw?.user?.additionalAttributes || {};
        const latStr = attributes.latitude;
        const lonStr = attributes.longitude;
        
        if (latStr && lonStr) {
          const latVal = parseFloat(latStr);
          const lonVal = parseFloat(lonStr);
          if (!isNaN(latVal) && !isNaN(lonVal)) {
            foundLat = latVal;
            foundLon = lonVal;
            foundNirvahakName = `${nir.user?.firstName || ''} ${nir.user?.lastName || ''}`.trim() || nir.name || 'Nirvahak';
            break;
          }
        }
      }

      if (foundLat !== null && foundLon !== null) {
        setTargetLocation({ latitude: foundLat, longitude: foundLon, name: foundNirvahakName });
      } else {
        setTargetLocation(null);
        setLocationStatus('not_configured');
      }

      if (matchingNirvahaks.length > 0) {
        setAssignedNirvahak(matchingNirvahaks[0]);
      } else {
        setAssignedNirvahak(null);
      }

    } catch (err) {
      console.error("Error loading student roster", err);
      setError("Failed to fetch students. Please try again.");
    } finally {
      setIsRosterLoading(false);
    }
  }, [isCustomOrg, selectedCenter, selectedClassId, selectedSubject, selectedDate, token, axios, classesList, matchingClasses]);

  // Load roster when dropdown selection changes
  useEffect(() => {
    loadRoster();
  }, [loadRoster]);

  // Load Nirvahaks on mount/orgId change
  useEffect(() => {
    const fetchNirvahaks = async () => {
      if (!orgId || !token) return;
      try {
        const res = await axios.get(`${configData.GET_USERS_BY_TYPE}/${orgId}`, {
          headers: { Authorization: `Bearer ${token}`, accept: 'application/hal+json' },
          params: { userType: 'FACULTY', pageSize: 300 }
        });
        const rawData = res.data?.items || res.data || [];
        const facultyList = Array.isArray(rawData) ? rawData : (Array.isArray(rawData.items) ? rawData.items : (Array.isArray(rawData.content) ? rawData.content : []));
        const filtered = facultyList.filter(f => {
          const type = f.facultyInfo?.facultyType || f.raw?.facultyInfo?.facultyType || '';
          return type === 'NIRVAHAK';
        }).map(f => {
          const raw = f.raw || f;
          const userObj = f.user || f.raw?.user || f;
          return {
            id: userObj.userId || userObj.id,
            name: `${userObj.firstName || ''} ${userObj.lastName || ''}`.trim() || f.name || 'Nirvahak',
            admissionId: f.facultyInfo?.employeeId || f.raw?.facultyInfo?.employeeId || '',
            raw: f
          };
        });
        setAllNirvahaksList(filtered);
      } catch (err) {
        console.error("Error fetching Nirvahaks:", err);
      }
    };
    fetchNirvahaks();
  }, [orgId, token, axios]);

  // Geofence helper functions
  const verifyLocationOfNirvahak = async (nir) => {
    if (!nir) {
      setGpsVerified(false);
      setLocationStatus('not_configured');
      return false;
    }

    setGpsVerifying(true);
    setLocationStatus('verifying');
    setDistance(null);

    const rawNir = nir.raw || nir;
    const attributes = rawNir.user?.additionalAttributes || 
                       rawNir.additionalAttributes || 
                       rawNir.raw?.user?.additionalAttributes || {};
                       
    const latStr = attributes.latitude;
    const lonStr = attributes.longitude;

    if (!latStr || !lonStr) {
      setGpsVerified(false);
      setGpsVerifying(false);
      setLocationStatus('not_configured');
      toast.warning(`Coordinates are not configured on Nirvahak "${nir.name}".`);
      return false;
    }

    const targetLat = parseFloat(latStr);
    const targetLon = parseFloat(lonStr);

    if (isNaN(targetLat) || isNaN(targetLon)) {
      setGpsVerified(false);
      setGpsVerifying(false);
      setLocationStatus('not_configured');
      toast.error(`Invalid coordinates configured on Nirvahak "${nir.name}".`);
      return false;
    }

    setTargetLocation({ latitude: targetLat, longitude: targetLon, name: nir.name });

    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        setGpsVerified(false);
        setGpsVerifying(false);
        setLocationStatus('permission_denied');
        toast.error("Geolocation is not supported by your browser.");
        resolve(false);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const dist = getDistanceInMeters(
            pos.coords.latitude,
            pos.coords.longitude,
            targetLat,
            targetLon
          );
          setCurrentLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
          setDistance(dist);

          if (dist <= 200) {
            setGpsVerified(true);
            setGpsVerifying(false);
            setLocationStatus('verified');
            resolve(true);
          } else {
            setGpsVerified(false);
            setGpsVerifying(false);
            setLocationStatus('out_of_range');
            toast.error(`You are not in the selected center's location. You are ${Math.round(dist)} meters away from Nirvahak "${nir.name}"'s coordinates.`);
            resolve(false);
          }
        },
        (err) => {
          console.error("GPS position fetch error:", err);
          setGpsVerified(false);
          setGpsVerifying(false);
          setLocationStatus('permission_denied');
          toast.error("Failed to retrieve your current location. Please grant GPS permissions in your browser.");
          resolve(false);
        },
        { enableHighAccuracy: true }
      );
    });
  };

  const checkGeofenceVerified = async () => {
    if (!isCustomOrg) return true;
    if (!selectedNirvahak) {
      setIsNirvahakModalOpen(true);
      toast.warning("Please select a Nirvahak to verify location before marking attendance.");
      return false;
    }
    if (gpsVerified) return true;
    
    const success = await verifyLocationOfNirvahak(selectedNirvahak);
    return success;
  };

  // Live Geofence Tracking Effect for Web
  /*
  useEffect(() => {
    if (!targetLocation) return;

    setLocationStatus('verifying');
    let watchId = null;

    if (!navigator.geolocation) {
      setLocationStatus('permission_denied');
      return;
    }

    const startWatching = () => {
      // First check
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const dist = getDistanceInMeters(
            pos.coords.latitude,
            pos.coords.longitude,
            targetLocation.latitude,
            targetLocation.longitude
          );
          setCurrentLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
          setDistance(dist);
          setLocationStatus(dist <= 200 ? 'verified' : 'out_of_range');
        },
        (err) => {
          console.error("Browser location fetch error:", err);
          setLocationStatus('permission_denied');
        },
        { enableHighAccuracy: true }
      );

      // Watch position updates
      watchId = navigator.geolocation.watchPosition(
        (pos) => {
          const dist = getDistanceInMeters(
            pos.coords.latitude,
            pos.coords.longitude,
            targetLocation.latitude,
            targetLocation.longitude
          );
          setCurrentLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
          setDistance(dist);
          setLocationStatus(dist <= 200 ? 'verified' : 'out_of_range');
        },
        (err) => {
          console.error("Browser location watch error:", err);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    };

    startWatching();

    return () => {
      if (watchId !== null) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, [targetLocation]);
  */

  // Handle single student status changes
  const handleStatusChange = async (studentId, status) => {
    if (!isToday) {
      toast.warning("Cannot modify attendance for dates other than today or yesterday.");
      return;
    }
    // If finalized and NOT admin, prevent editing
    if (recordState === 'final' && !isAdmin) {
      toast.warning("Cannot modify finalized attendance. Please raise a correction request.");
      return;
    }

    const verified = await checkGeofenceVerified();
    if (!verified) return;

    setAttendanceRecords(prev => {
      const current = prev[studentId];
      const newStatus = current && current.status === status ? '' : status;
      return {
        ...prev,
        [studentId]: {
          ...current,
          status: newStatus
        }
      };
    });
  };

  // Handle single student remark changes
  const handleRemarkChange = async (studentId, remark) => {
    if (!isToday) return;
    if (recordState === 'final' && !isAdmin) return;
    
    const verified = await checkGeofenceVerified();
    if (!verified) return;

    setAttendanceRecords(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        remark
      }
    }));
  };

  // Bulk operation: Toggle Mark All Present / Unmark All
  const handleMarkAllPresent = async () => {
    if (!isToday) {
      toast.warning("Attendance can only be marked for today or yesterday.");
      return;
    }
    if (recordState === 'final' && !isAdmin) {
      toast.warning("Roster is locked.");
      return;
    }
    
    const verified = await checkGeofenceVerified();
    if (!verified) return;
    
    const areAllPresent = students.length > 0 && students.every(s => attendanceRecords[s.id]?.status === 'Present');
    const targetStatus = areAllPresent ? '' : 'Present';

    setAttendanceRecords(prev => {
      const updated = { ...prev };
      students.forEach(s => {
        updated[s.id] = {
          ...updated[s.id],
          status: targetStatus
        };
      });
      return updated;
    });

    if (areAllPresent) {
      toast.info("All student attendance records un-marked.");
    } else {
      toast.info("All active students marked as Present.");
    }
  };

  const handleQuickRegisterLocation = async () => {
    if (!assignedNirvahak) {
      toast.error("No Nirvahak is currently assigned to this KV Center/Class. Please assign a Nirvahak first.");
      return;
    }

    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser.");
      return;
    }

    setIsRosterLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const currentLat = pos.coords.latitude;
        const currentLon = pos.coords.longitude;

        try {
          const rawNir = assignedNirvahak.raw || assignedNirvahak;
          const userObj = rawNir.user || rawNir;
          
          const cleanCenter = (isCustomOrg ? selectedCenter : (classesList.find(c => c.classId === selectedClassId)?.sectionName || '')).toLowerCase().trim();
          const centerKey = cleanCenter.replace(/[^a-z0-9]/g, '_');

          const payload = {
            user: {
              ...userObj,
              additionalAttributes: {
                ...(userObj.additionalAttributes || {}),
                [`latitude_${centerKey}`]: String(currentLat),
                [`longitude_${centerKey}`]: String(currentLon),
              }
            },
            facultyInfo: rawNir.facultyInfo
          };

          const userId = userObj.userId || userObj.id;
          await axios.put(`${configData.UPDATE_USER}/${userId}`, payload, {
            headers: { Authorization: `Bearer ${token}` }
          });

          setTargetLocation({
            latitude: currentLat,
            longitude: currentLon,
            name: `${userObj.firstName || ''} ${userObj.lastName || ''}`.trim() || assignedNirvahak.name
          });
          
          toast.success("Location registered successfully for this KV Center/Class!");
        } catch (err) {
          console.error("Failed to update Nirvahak location profile:", err);
          toast.error("Failed to register location coordinates.");
        } finally {
          setIsRosterLoading(false);
        }
      },
      (err) => {
        console.error("GPS Position fetch error:", err);
        toast.error("Failed to fetch current location. Please grant GPS access.");
        setIsRosterLoading(false);
      },
      { enableHighAccuracy: true }
    );
  };

  // Perform save (mode = 'draft' or 'final')
  const handleSaveAttendance = async (mode, isBypassing = false, forcedReason = '') => {
    if (!isToday) {
      toast.error("Attendance can only be saved for today or yesterday's date.");
      return;
    }
    const verified = await checkGeofenceVerified();
    if (!verified) return;

    // Validation: Enforce future dates constraint
    const todayStr = new Date().toISOString().split('T')[0];
    if (selectedDate > todayStr && settings && !settings.allowFutureDates) {
      toast.error("Attendance cannot be marked for future dates.");
      return;
    }

    // Validation: Check duplicate final submission
    if (recordState === 'final' && mode === 'final' && !isAdmin) {
      toast.error("Duplicate final submission is prevented unless you have Administrator override permission.");
      return;
    }

    setIsRosterLoading(true);
    try {
      const selectedSubObj = subjectsList.find(s => s.courseCode === selectedSubject);
      const subjectNameText = isSmallOrg ? 'General' : (selectedSubObj ? selectedSubObj.courseName : (selectedSubject || 'General'));

      // 1. Submit/Save/Delete to backend database in parallel
      if (token) {
        const backendRequests = students.map(async s => {
          const recordStateForStudent = attendanceRecords[s.id] || { status: '', remark: '', attendanceId: null };
          
          if (!recordStateForStudent.status) {
            if (recordStateForStudent.attendanceId) {
              try {
                await deleteAttendanceRecord(recordStateForStudent.attendanceId);
              } catch (err) {
                console.warn(`Failed to delete record ${recordStateForStudent.attendanceId}`, err);
              }
              return { userId: s.id, attendanceId: null };
            }
            return null;
          }

          const resolvedRemark = forcedReason.trim()
            ? `${recordStateForStudent.remark || ''} [GEO_BYPASS: ${forcedReason.trim()}]`.trim()
            : recordStateForStudent.remark || '';

          const payload = {
            orgId: orgId || "org_demo_1",
            classId: s.classId, // student-specific classId
            userId: s.id,
            academicYear: "2025-2026", // default academic year format
            attendanceDate: selectedDate,
            checkInTime: new Date().toISOString(),
            userType: "STUDENT",
            attendanceStatus: mapStatusToBackend(recordStateForStudent.status),
            classOrLocation: s.className || "KV Center",
            subject: subjectNameText,
            remarks: resolvedRemark,
            attendanceMethod: forcedReason.trim() ? "BYPASS_GEO" : "MANUAL_ENTRY",
            createdBy: user?.displayName || user?.name || "Teacher Vinay",
            lastUpdatedBy: user?.displayName || user?.name || "Teacher Vinay"
          };

          if (recordStateForStudent.attendanceId) {
            // Update existing record
            return updateAttendanceRecord(recordStateForStudent.attendanceId, payload);
          } else {
            // Create new record
            return createAttendanceRecord(payload);
          }
        });

        // Run all API calls in parallel for top speed
        const results = await Promise.all(backendRequests);
        
        // Refresh local state with new IDs from backend
        const updatedRecords = { ...attendanceRecords };
        results.forEach(res => {
          if (res && res.userId) {
            updatedRecords[res.userId] = {
              ...updatedRecords[res.userId],
              attendanceId: res.attendanceId
            };
          }
        });
        setAttendanceRecords(updatedRecords);
      }

      setRecordState(mode);

      if (mode === 'draft') {
        toast.success("Attendance draft saved to database successfully.");
      } else {
        toast.success("Attendance finalized, synchronized with database, and locked successfully.");
        
        // Auto-notify placeholder trigger
        if (settings?.notifyAbsence) {
          const absentees = students.filter(s => {
            const rec = attendanceRecords[s.id];
            return rec && rec.status === 'Absent';
          });
          if (absentees.length > 0) {
            console.log(`[ERP Alert] Sent automated SMS/Email absence alerts to parents of: ${absentees.map(a => a.name).join(', ')}`);
          }
        }
      }
    } catch (err) {
      console.error("Error saving attendance to database", err);
      toast.error("Failed to synchronize attendance with database.");
    } finally {
      setIsRosterLoading(false);
    }
  };

  // Open correction request modal
  const openCorrectionModal = (student) => {
    setCorrectionData({
      studentId: student.id,
      studentName: student.name,
      admissionNumber: student.admissionNumber,
      oldStatus: attendanceRecords[student.id]?.status || 'Absent',
      newStatus: 'Present',
      reason: ''
    });
    setShowCorrectionModal(true);
  };

  // Submit correction request
  const submitCorrectionRequest = (e) => {
    e.preventDefault();
    if (!correctionData.reason.trim()) {
      toast.error("Please provide a reason for the attendance correction.");
      return;
    }

    const studentObj = students.find(s => s.id === correctionData.studentId);
    if (!studentObj) return;

    const key = `${studentObj.classId}_${selectedDate}_${isSmallOrg ? 'GENERAL' : selectedSubject}`;
    const corrections = getStoredCorrections();

    const newRequest = {
      id: `req_${Date.now()}`,
      recordKey: key,
      classId: studentObj.classId,
      className: studentObj.className || (isCustomOrg ? "Select KV Center" : "Select Class"),
      date: selectedDate,
      subjectCode: isSmallOrg ? 'GENERAL' : selectedSubject,
      subjectName: isSmallOrg ? 'General' : (subjectsList.find(s => s.courseCode === selectedSubject)?.courseName || selectedSubject),
      studentId: correctionData.studentId,
      studentName: correctionData.studentName,
      admissionNumber: correctionData.admissionNumber,
      oldStatus: correctionData.oldStatus,
      newStatus: correctionData.newStatus,
      reason: correctionData.reason.trim(),
      requestedBy: `${user?.displayName || user?.name || "Teacher Vinay"} (${userRole})`,
      requestedAt: new Date().toISOString(),
      status: "Pending"
    };

    corrections.unshift(newRequest);
    saveStoredCorrections(corrections);

    setShowCorrectionModal(false);
    toast.success("Attendance correction request submitted to Administrator for review.");
  };

  // Calculations for stats bar
  const counts = { present: 0, absent: 0, late: 0, leave: 0 };
  students.forEach(s => {
    const status = attendanceRecords[s.id]?.status;
    if (status === 'Present') counts.present++;
    else if (status === 'Absent') counts.absent++;
    else if (status === 'Late') counts.late++;
    else if (status === 'Leave') counts.leave++;
  });

  // Filter student list by search query
  const filteredStudents = students.filter(s =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.admissionNumber.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredNirvahaks = allNirvahaksList.filter(n => {
    const q = nirvahakSearchQuery.toLowerCase().trim();
    if (!q) return true;
    return n.name.toLowerCase().includes(q) || n.admissionId.toLowerCase().includes(q);
  });

  return (
    <div className="attendance-container">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="attendance-header">
        <div>
          <h1>Daily Attendance</h1>
          <p>
            {isSmallOrg
              ? "Select date and class to mark or review attendance."
              : isCustomOrg 
                ? "Select date, KV center, and subject to mark or review attendance."
                : "Select date, class, and subject to mark or review attendance."
            }
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span className="role-badge">{userRole} Profile</span>
        </div>
      </div>

      {/* Filter Section */}
      <div className="filters-card">
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '10px' }}>Loading filter data...</div>
        ) : (
          <div className="filters-grid">
            <div className="filter-group">
              <label>Attendance Date</label>
              <input
                type="date"
                className="filter-control"
                value={selectedDate}
                max={new Date().toISOString().split('T')[0]} // Front-end max today validation
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>
            
            <div className="filter-group">
              <label>{isCustomOrg ? "KV Center" : "Class"}</label>
              {isCustomOrg ? (
                <select
                  className="filter-control"
                  value={selectedCenter}
                  onChange={(e) => {
                    setSelectedCenter(e.target.value);
                    setSelectedNirvahak(null);
                    setGpsVerified(false);
                  }}
                >
                  {uniqueCenters.map(center => (
                    <option key={center} value={center}>{center}</option>
                  ))}
                </select>
              ) : (
                <select
                  className="filter-control"
                  value={selectedClassId}
                  onChange={(e) => {
                    setSelectedClassId(e.target.value);
                    setSelectedNirvahak(null);
                    setGpsVerified(false);
                  }}
                >
                  {classesList.map(cls => (
                    <option key={cls.classId} value={cls.classId}>{cls.name}</option>
                  ))}
                </select>
              )}
            </div>

            {!isSmallOrg && (
              <div className="filter-group">
                <label>Subject / Period</label>
                <select
                  className="filter-control"
                  value={selectedSubject}
                  onChange={(e) => setSelectedSubject(e.target.value)}
                >
                  {subjectsList.map(s => (
                    <option key={s.courseCode} value={s.courseCode}>{s.courseName} - {s.period || 'General'}</option>
                  ))}
                </select>
              </div>
            )}

            {isToday && isCustomOrg && (
              <div className="filter-group">
                <label>Nirvahak Geofence</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button 
                    className="filter-control"
                    style={{ textAlign: 'left', backgroundColor: '#fff', cursor: 'pointer', border: '1px solid #cbd5e1' }}
                    onClick={() => setIsNirvahakModalOpen(true)}
                  >
                    {selectedNirvahak ? `Nirvahak: ${selectedNirvahak.name}` : 'Select Nirvahak to Verify...'}
                  </button>
                </div>
              </div>
            )}

            <div>
              <button className="btn-fetch" onClick={loadRoster}>
                Reload Roster
              </button>
            </div>
          </div>
        )}
      </div>

      {isToday && isCustomOrg && (
        <div style={{
          margin: '0 0 16px 0',
          padding: '12px 16px',
          borderRadius: '8px',
          border: '1px solid',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontWeight: 600,
          fontSize: '13px',
          ...(() => {
            if (!selectedNirvahak) return { backgroundColor: '#fffbeb', borderColor: '#fde68a', color: '#92400e' };
            if (locationStatus === 'verifying') return { backgroundColor: '#f8fafc', borderColor: '#cbd5e1', color: '#475569' };
            if (locationStatus === 'verified') return { backgroundColor: '#eff6ff', borderColor: '#bfdbfe', color: '#1e40af' };
            if (locationStatus === 'out_of_range') return { backgroundColor: '#fef2f2', borderColor: '#fca5a5', color: '#991b1b' };
            if (locationStatus === 'not_configured') return { backgroundColor: '#fffbeb', borderColor: '#fde68a', color: '#92400e' };
            return { backgroundColor: '#fef2f2', borderColor: '#fca5a5', color: '#991b1b' };
          })()
        }}>
          {!selectedNirvahak ? (
            <span>ℹ️ Please select a Nirvahak in the filters to verify location.</span>
          ) : (
            <>
              {locationStatus === 'verifying' && (
                <>
                  <div className="spinner-small" style={{
                    width: '14px',
                    height: '14px',
                    border: '2px solid #64748b',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <span>Verifying location coordinates for Nirvahak "{selectedNirvahak.name}"...</span>
                </>
              )}
              {locationStatus === 'verified' && (
                <>
                  <span>🟢</span>
                  <span>Location Verified {distance !== null ? `(${Math.round(distance)}m away from Nirvahak "${selectedNirvahak.name}" coordinates)` : ''}</span>
                </>
              )}
              {locationStatus === 'out_of_range' && (
                <>
                  <span>🔴</span>
                  <span>Out of Range {distance !== null ? `(${Math.round(distance)}m away from Nirvahak "${selectedNirvahak.name}" coordinates)` : ''}</span>
                </>
              )}
              {locationStatus === 'not_configured' && (
                <span>ℹ️ Location Not Configured on Nirvahak "{selectedNirvahak.name}" for this center/class</span>
              )}
              {locationStatus === 'permission_denied' && (
                <>
                  <span>⚠️</span>
                  <span>GPS Permission Denied</span>
                </>
              )}
            </>
          )}
        </div>
      )}



      {/* Roster Area */}
      <div className="roster-card">
        <div className="roster-header">
          <div className="roster-title-area">
            <h2>Student Attendance Sheet</h2>
            <p>{filteredStudents.length} active students in list.</p>
          </div>

          <div className="bulk-actions">
            <div className="search-box">
              <span className="search-icon-pos"><SearchIcon /></span>
              <input
                type="text"
                placeholder="Search name or adm no..."
                className="search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            {isToday && (recordState !== 'final' || isAdmin) ? (
              <button className="btn-bulk-present" onClick={handleMarkAllPresent}>
                {students.length > 0 && students.every(s => attendanceRecords[s.id]?.status === 'Present') ? "Unmark All" : "Mark All Present"}
              </button>
            ) : null}
          </div>
        </div>

        {isRosterLoading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>{isCustomOrg ? "Gathering KV Center student registry..." : "Gathering Class student registry..."}</p>
          </div>
        ) : filteredStudents.length === 0 ? (
          <div className="empty-container">
            <WarningIcon />
            <h3>No Active Students Found</h3>
            <p>
              {isCustomOrg
                ? "Make sure this KV Center is populated or try modifying the search filter query."
                : "Make sure this Class is populated or try modifying the search filter query."
              }
            </p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="roster-table">
              <thead>
                <tr>
                  <th style={{ width: '60px' }}>Roll</th>
                  <th style={{ width: '220px' }}>Student Info</th>
                  <th>Attendance Status</th>
                  <th style={{ width: '220px' }}>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents.map((student, idx) => {
                  const record = attendanceRecords[student.id] || { status: 'Present', remark: '' };
                  const isDisabled = (recordState === 'final' && !isAdmin) || !isToday;

                  return (
                    <tr key={student.id}>
                      <td style={{ fontWeight: 600, color: '#64748b' }}>{idx + 1}</td>
                      <td>
                        <div className="student-info-cell">
                          <span className="student-name">{student.name}</span>
                          <span className="student-adm">{student.admissionNumber}</span>
                        </div>
                      </td>
                      <td>
                        <div className="status-buttons-group">
                          <button
                            disabled={isDisabled}
                            className={`status-btn present ${record.status === 'Present' ? 'active' : ''}`}
                            onClick={() => handleStatusChange(student.id, 'Present')}
                          >
                            <CheckCircleIcon style={{ fontSize: '16px' }} /> Present
                          </button>
                          
                          <button
                            disabled={isDisabled}
                            className={`status-btn absent ${record.status === 'Absent' ? 'active' : ''}`}
                            onClick={() => handleStatusChange(student.id, 'Absent')}
                          >
                            <CancelIcon style={{ fontSize: '16px' }} /> Absent
                          </button>

                          <button
                            disabled={isDisabled}
                            className={`status-btn late ${record.status === 'Late' ? 'active' : ''}`}
                            onClick={() => handleStatusChange(student.id, 'Late')}
                          >
                            <ScheduleIcon style={{ fontSize: '16px' }} /> Late
                          </button>

                          <button
                            disabled={isDisabled}
                            className={`status-btn leave ${record.status === 'Leave' ? 'active' : ''}`}
                            onClick={() => handleStatusChange(student.id, 'Leave')}
                          >
                            <BeachAccessIcon style={{ fontSize: '16px' }} /> Leave
                          </button>
                        </div>
                      </td>
                      <td>
                        <input
                          type="text"
                          placeholder="Optional remarks..."
                          className="remark-input"
                          value={record.remark || ''}
                          disabled={isDisabled}
                          onChange={(e) => handleRemarkChange(student.id, e.target.value)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer Actions */}
        {!isRosterLoading && filteredStudents.length > 0 && (
          <div className="submission-footer">
            <div className="summary-stats">
              <span className="stat-pill p">Present: {counts.present}</span>
              <span className="stat-pill a">Absent: {counts.absent}</span>
              <span className="stat-pill l">Late: {counts.late}</span>
              <span className="stat-pill v">Leave: {counts.leave}</span>
            </div>

             <div className="action-buttons">
              {isToday && (recordState !== 'final' || isAdmin) && (
                <>
                  <button className="btn-draft" onClick={() => handleSaveAttendance('draft')}>
                    <SaveIcon style={{ fontSize: '18px', marginRight: '6px', verticalAlign: 'middle' }} /> Save Draft
                  </button>
                  <button className="btn-submit" onClick={() => handleSaveAttendance('final')}>
                    <CloudUploadIcon style={{ fontSize: '18px', marginRight: '6px', verticalAlign: 'middle' }} /> Final Submit
                  </button>
                </>
              )}
              {(!isToday || (recordState === 'final' && !isAdmin)) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b', fontSize: '14px', fontWeight: 600 }}>
                  <SecurityIcon style={{ color: '#10b981' }} /> {!isToday ? 'Attendance History (Read Only)' : 'Attendance Locked & Submitted'}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Correction Modal */}
      {showCorrectionModal && (
        <div className="correction-modal-backdrop">
          <div className="correction-modal">
            <div className="modal-header">
              <h3>Raise Attendance Correction</h3>
              <button className="close-modal-btn" onClick={() => setShowCorrectionModal(false)}>
                <CloseIcon />
              </button>
            </div>
            <form onSubmit={submitCorrectionRequest}>
              <div className="modal-body">
                <div className="form-group-modal">
                  <label>Student Name</label>
                  <input type="text" value={correctionData.studentName} disabled />
                </div>

                <div className="form-group-modal">
                  <label>Admission Number</label>
                  <input type="text" value={correctionData.admissionNumber} disabled />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group-modal">
                    <label>Current Status</label>
                    <input type="text" value={correctionData.oldStatus} disabled />
                  </div>
                  <div className="form-group-modal">
                    <label>Desired Status</label>
                    <select
                      value={correctionData.newStatus}
                      onChange={(e) => setCorrectionData(prev => ({ ...prev, newStatus: e.target.value }))}
                    >
                      <option value="Present">Present</option>
                      <option value="Absent">Absent</option>
                      <option value="Late">Late</option>
                      <option value="Leave">Leave</option>
                    </select>
                  </div>
                </div>

                <div className="form-group-modal">
                  <label>Reason for Correction</label>
                  <textarea
                    required
                    placeholder="Provide a detailed explanation for this change (e.g. Student late because of medical checkup, had bus breakdown...)"
                    value={correctionData.reason}
                    onChange={(e) => setCorrectionData(prev => ({ ...prev, reason: e.target.value }))}
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-draft" style={{ padding: '8px 16px' }} onClick={() => setShowCorrectionModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" style={{ padding: '8px 20px', backgroundColor: '#e28743' }}>
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Geofence Bypass Modal */}
      {showBypassModal && (
        <div className="correction-modal-backdrop">
          <div className="correction-modal" style={{ maxWidth: '420px' }}>
            <div className="modal-header">
              <h3>Location Bypass Request</h3>
              <button className="close-modal-btn" onClick={() => { setShowBypassModal(false); setPendingSaveMode(null); }}>
                <CloseIcon />
              </button>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              if (!bypassReason.trim()) {
                toast.error("Please enter a bypass justification.");
                return;
              }
              const mode = pendingSaveMode || 'draft';
              const reason = bypassReason;
              setShowBypassModal(false);
              setBypassReason('');
              setPendingSaveMode(null);
              handleSaveAttendance(mode, true, reason);
            }}>
              <div className="modal-body">
                <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '14px', lineHeight: 1.5 }}>
                  You are currently marking attendance from outside the center range or coordinates are not configured. To proceed, please provide a justification for this bypass.
                </p>
                <div className="form-group-modal">
                  <label>Justification Reason</label>
                  <textarea
                    required
                    style={{ minHeight: '80px', padding: '10px' }}
                    placeholder="e.g., GPS inaccuracy indoors, remote supervisor authorization, emergency exception..."
                    value={bypassReason}
                    onChange={(e) => setBypassReason(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer" style={{ gap: '10px' }}>
                <button type="button" className="btn-draft" style={{ padding: '8px 16px' }} onClick={() => { setShowBypassModal(false); setPendingSaveMode(null); }}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" style={{ padding: '8px 20px', backgroundColor: '#0B4A8B' }}>
                  Submit & Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Nirvahak Selector Modal */}
      {isNirvahakModalOpen && (
        <div className="correction-modal-backdrop" style={{ zIndex: 1000 }}>
          <div className="correction-modal" style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h3>Select Nirvahak to Verify Location</h3>
              <button className="close-modal-btn" onClick={() => setIsNirvahakModalOpen(false)}>
                <CloseIcon />
              </button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '15px' }}>
              <div style={{ position: 'relative' }}>
                <input 
                  type="text" 
                  placeholder="Search Nirvahak by name or employee ID..." 
                  className="filter-control" 
                  style={{ width: '100%', paddingLeft: '32px' }}
                  value={nirvahakSearchQuery}
                  onChange={(e) => setNirvahakSearchQuery(e.target.value)}
                />
                <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>🔍</span>
              </div>

              <div style={{ maxHeight: '280px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '10px' }}>
                {filteredNirvahaks.map(n => (
                  <button
                    key={n.id}
                    className="modal-option-btn"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      textAlign: 'left',
                      background: selectedNirvahak?.id === n.id ? '#eff6ff' : '#fff',
                      border: selectedNirvahak?.id === n.id ? '1px solid #bfdbfe' : '1px solid #e2e8f0',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '2px'
                    }}
                    onClick={() => {
                      setSelectedNirvahak(n);
                      setIsNirvahakModalOpen(false);
                      verifyLocationOfNirvahak(n);
                    }}
                  >
                    <span style={{ fontWeight: 'bold', color: '#1e293b' }}>{n.name}</span>
                    {n.admissionId && (
                      <span style={{ fontSize: '11px', color: '#64748b' }}>Emp ID: {n.admissionId}</span>
                    )}
                  </button>
                ))}
                {filteredNirvahaks.length === 0 && (
                  <div style={{ textAlign: 'center', color: '#94a3b8', padding: '20px 0' }}>
                    No Nirvahaks found.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarkAttendance;
